import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { categories, articles, search, comments, versions } from "@/lib/kbApi";

// Categories
export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => categories.list(),
  });
}

// Articles
export function useArticles(categorySlug?: string) {
  return useQuery({
    queryKey: ["articles", { category: categorySlug }],
    queryFn: () => articles.list({ category: categorySlug }),
  });
}

export function useArticle(slug: string) {
  return useQuery({
    queryKey: ["article", slug],
    queryFn: () => articles.getBySlug(slug),
    enabled: !!slug,
  });
}

// Search
export function useSearch(query: string) {
  return useQuery({
    queryKey: ["search", query],
    queryFn: () => search.query(query),
    enabled: !!query.trim(),
  });
}

// Comments
export function useComments(articleId: number) {
  return useQuery({
    queryKey: ["comments", articleId],
    queryFn: () => comments.list(articleId),
    enabled: !!articleId,
  });
}

// Versions
export function useVersions(articleId: number) {
  return useQuery({
    queryKey: ["versions", articleId],
    queryFn: () => versions.list(articleId),
    enabled: !!articleId,
  });
}

// Article mutations
export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: articles.create,
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: any }) =>
      articles.update(slug, data),
    onSuccess: (result, { slug }) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["article", slug] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: articles.delete,
    onSuccess: (result, slug) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      // Remove the deleted article from cache
      queryClient.removeQueries({ queryKey: ["article", slug] });
    },
  });
}